import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { setMetaTags } from '../utils/seo';

const tiers = [
  {
    name: 'Free',
    price: '₹0',
    period: 'forever',
    desc: 'Try the full product for small teams',
    features: [
      '1 user',
      '3 active jobs',
      '100 candidates',
      'AI resume parsing',
      'Email support',
    ],
    cta: 'Start free',
    accent: 'border-slate-700',
    popular: false,
  },
  {
    name: 'Starter',
    price: '₹2,999',
    period: '/ month',
    desc: 'For growing recruitment teams',
    features: [
      '5 users',
      '25 active jobs',
      '5,000 candidates',
      'AI matching included',
      'Bulk Excel import',
      'Priority email support',
    ],
    cta: 'Start 14-day trial',
    accent: 'border-slate-700',
    popular: false,
  },
  {
    name: 'Pro',
    price: '₹7,999',
    period: '/ month',
    desc: 'Most popular for staffing agencies',
    features: [
      '20 users',
      '100 active jobs',
      '50,000 candidates',
      'Recruiter Command Center',
      '"Best 10" AI shortlist',
      'Live revenue metrics',
      'Bulk re-upload',
      'Phone + email support',
    ],
    cta: 'Start 14-day trial',
    accent: 'border-teal-400',
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For large staffing networks',
    features: [
      'Unlimited users',
      'Unlimited jobs & candidates',
      'SSO / SAML',
      'Dedicated success manager',
      'SLA-backed uptime',
      'White-label option',
      'Custom integrations',
    ],
    cta: 'Contact us',
    accent: 'border-slate-700',
    popular: false,
  },
];

const Pricing = () => {
  useEffect(() => {
    setMetaTags({
      title: 'Pricing — InterviewWala ATS',
      description:
        'Simple INR pricing for Indian staffing firms. Free forever for solo recruiters. Starter ₹2,999/mo, Pro ₹7,999/mo. No credit card required.',
      url: '/pricing',
      image: '/demo/04-settings.jpg',
      type: 'website',
    });
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="pricing-page">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-30 backdrop-blur-md bg-slate-950/60 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo-home">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-slate-900" />
            </div>
            <span
              className="text-lg font-semibold"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              InterviewWala
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/login"
              className="text-sm text-slate-300 hover:text-white transition px-3 py-2"
            >
              Sign in
            </Link>
            <Link to="/signup">
              <Button
                size="sm"
                className="bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold"
              >
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1
              className="text-4xl sm:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="pricing-title"
            >
              Simple, predictable <span className="text-teal-400">pricing</span>
            </h1>
            <p className="text-slate-300 max-w-xl mx-auto">
              Start free, scale when you do. All plans include AI resume parsing and unlimited
              client orgs.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((t) => (
              <div
                key={t.name}
                data-testid={`pricing-tier-${t.name.toLowerCase()}`}
                className={`relative rounded-2xl border-2 ${t.accent} bg-slate-900/40 p-6 flex flex-col ${
                  t.popular ? 'shadow-2xl shadow-teal-500/10' : ''
                }`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 text-[10px] font-bold uppercase tracking-wider">
                    Most popular
                  </span>
                )}
                <div className="mb-4">
                  <h3 className="text-xl font-semibold mb-1">{t.name}</h3>
                  <p className="text-xs text-slate-400 leading-snug min-h-[32px]">
                    {t.desc}
                  </p>
                </div>
                <div className="mb-5">
                  <span className="text-4xl font-bold">{t.price}</span>
                  {t.period && (
                    <span className="text-sm text-slate-400 ml-1">{t.period}</span>
                  )}
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {t.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-slate-300"
                    >
                      <CheckCircle2 className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {t.name === 'Enterprise' ? (
                  <a
                    href="mailto:hello@interviewwala.com"
                    data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  >
                    <Button
                      className="w-full bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"
                    >
                      {t.cta}
                    </Button>
                  </a>
                ) : (
                  <Link to="/signup" data-testid={`pricing-cta-${t.name.toLowerCase()}`}>
                    <Button
                      className={`w-full ${
                        t.popular
                          ? 'bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold'
                          : 'bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700'
                      }`}
                    >
                      {t.cta} {t.popular && <ArrowRight className="w-4 h-4 ml-1" />}
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-10">
            Prices in INR. International pricing available — contact us. All plans GST-inclusive.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
